import React, { useState, useMemo } from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Button,
  Chip,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { 
  Search, 
  Close, 
  Add, 
  WhatsApp, 
  GridView, 
  ViewList,
  FilterList 
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { getAllProducts, productCategories, formatPrice } from "../data/products";
import { useQuote } from "../context/QuoteContext";
import SEO from "../components/SEO";
import { seoConfig } from "../data/seoConfig";

const ProductContainer = styled(Container)(({ theme }) => ({
  marginTop: "2rem",
  marginBottom: "4rem",
}));

const FilterSection = styled(Box)(({ theme }) => ({
  background: theme.palette.background.paper,
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  marginBottom: theme.spacing(4),
  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
}));

const ViewControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
    alignItems: 'stretch',
  },
}));

const ProductPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [notes, setNotes] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  
  const { addToQuote } = useQuote();
  const allProducts = useMemo(() => getAllProducts(), []);

  const filteredProducts = useMemo(() => {
    let filtered = allProducts;

    if (selectedCategory) {
      filtered = filtered.filter(product => product.categoryId === parseInt(selectedCategory));
    }

    if (searchQuery) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.categoryName.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  }, [allProducts, selectedCategory, searchQuery]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId) {
      setSearchParams({ category: categoryId });
    } else {
      setSearchParams({});
    }
  };

  const handleViewModeChange = (event, newViewMode) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleAddToQuote = (product) => {
    setSelectedProduct(product);
    setQuantity(product.minOrder.toString());
    setNotes('');
    setOpenDialog(true);
  };

  const handleConfirmAddToQuote = () => {
    if (selectedProduct && quantity) {
      addToQuote({
        productId: selectedProduct.id,
        name: selectedProduct.name,
        price: selectedProduct.price,
        unit: selectedProduct.unit,
        quantity: parseInt(quantity),
        notes: notes,
        categoryName: selectedProduct.categoryName
      });
      setOpenDialog(false);
      setSelectedProduct(null);
    }
  };

  const handleWhatsApp = (product) => {
    const message = `Hi, I'm interested in ${product.name}. Could you please provide more details?`;
    const whatsappUrl = `https://wa.me/919764166071?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const ProductGridItem = ({ product, index }) => (
    <Grid item xs={12} sm={6} md={4} key={product.id}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
      >
        <Card
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
            },
          }}
        >
          <CardMedia
            component="img"
            height="200"
            image={product.image || "/assets/products/placeholder.jpg"}
            alt={product.name}
            sx={{ objectFit: "cover" }}
            onError={(e) => {
              e.target.src = "/assets/products/placeholder.jpg";
            }}
          />
          <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, flexGrow: 1, fontSize: '1rem' }}>
                {product.name}
              </Typography>
              <Chip
                label={product.categoryName}
                size="small"
                color="secondary"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            </Box>
            
            <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 1, fontSize: '1.1rem' }}>
              {formatPrice(product.price)}
              <Typography component="span" variant="body2" color="text.secondary">
                /{product.unit}
              </Typography>
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontSize: '0.8rem' }}>
              Min Order: {product.minOrder} {product.unit}
              {product.size && ` • Size: ${product.size}`}
            </Typography>
            
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 2, flexGrow: 1, lineHeight: 1.4, fontSize: '0.85rem' }}
            >
              {product.description.substring(0, 100)}...
            </Typography>
            
            {/* Fixed Button Layout - Same line horizontal */}
            <Box sx={{ 
              display: "flex", 
              gap: 0.5, 
              justifyContent: "space-between",
              alignItems: "stretch" 
            }}>
              <Button
                component={RouterLink}
                to={`/product/${product.id}`}
                variant="outlined"
                size="small"
                sx={{ 
                  fontSize: '0.7rem', 
                  py: 0.5, 
                  px: 1,
                  flex: 1,
                  minWidth: 0
                }}
              >
                Details
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleAddToQuote(product)}
                startIcon={<Add sx={{ fontSize: '0.8rem' }} />}
                sx={{ 
                  fontSize: '0.7rem', 
                  py: 0.5, 
                  px: 1,
                  flex: 1,
                  minWidth: 0
                }}
              >
                Quote
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleWhatsApp(product)}
                startIcon={<WhatsApp sx={{ fontSize: '0.8rem' }} />}
                sx={{ 
                  fontSize: '0.7rem',
                  py: 0.5,
                  px: 1,
                  flex: 1,
                  minWidth: 0,
                  backgroundColor: '#25D366',
                  '&:hover': {
                    backgroundColor: '#1EA952',
                  }
                }}
              >
                Chat
              </Button>
            </Box>
          </CardContent>
        </Card>
      </motion.div>
    </Grid>
  );

  const ProductListItem = ({ product, index }) => (
    <motion.div
      key={product.id}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: index * 0.05 }}
    >
      <Card sx={{ mb: 2 }}>
        <ListItem alignItems="flex-start" sx={{ p: 2 }}>
          <ListItemAvatar>
            <Avatar
              variant="rounded"
              src={product.image || "/assets/products/placeholder.jpg"}
              sx={{ width: 80, height: 80, mr: 2 }}
            />
          </ListItemAvatar>
          <ListItemText
            sx={{ flex: 1 }}
            primary={
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                  {product.name}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, ml: 2 }}>
                  <Chip label={product.categoryName} size="small" color="secondary" variant="outlined" />
                  {/* Buttons on the right for list view */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      component={RouterLink}
                      to={`/product/${product.id}`}
                      variant="outlined"
                      size="small"
                      sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                    >
                      Details
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleAddToQuote(product)}
                      startIcon={<Add sx={{ fontSize: '0.8rem' }} />}
                      sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                    >
                      Quote
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => handleWhatsApp(product)}
                      startIcon={<WhatsApp sx={{ fontSize: '0.8rem' }} />}
                      sx={{ 
                        fontSize: '0.75rem',
                        minWidth: 'auto',
                        backgroundColor: '#25D366',
                        '&:hover': { backgroundColor: '#1EA952' }
                      }}
                    >
                      Chat
                    </Button>
                  </Box>
                </Box>
              </Box>
            }
            secondary={
              <Box>
                <Typography variant="h6" color="primary" sx={{ fontWeight: 700, mb: 1 }}>
                  {formatPrice(product.price)}/{product.unit}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Min Order: {product.minOrder} {product.unit} {product.size && `• Size: ${product.size}`}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {product.description.substring(0, 200)}...
                </Typography>
              </Box>
            }
          />
        </ListItem>
      </Card>
    </motion.div>
  );

  // Enhanced SEO function with dynamic keywords
  const getEnhancedSEO = () => {
    const baseKeywords = seoConfig.pages.products.keywords;
    const categoryKeywords = selectedCategory ?
      seoConfig.productCategories[selectedCategory]?.keywords || "" : "";
    const searchKeywords = filteredProducts
      .slice(0, 10)
      .map(product => `${product.name}, ${product.categoryName}`)
      .join(', ');
    const combinedKeywords = [
      baseKeywords,
      categoryKeywords,
      searchKeywords,
      ...seoConfig.longTailKeywords.regional,
      ...seoConfig.longTailKeywords.businessType,
      ...seoConfig.longTailKeywords.industry,
      ...seoConfig.longTailKeywords.applications
    ].filter(Boolean).join(', ');

    return {
      title: selectedCategory ?
        seoConfig.productCategories[selectedCategory]?.title || seoConfig.pages.products.title :
        seoConfig.pages.products.title,
      description: selectedCategory ?
        seoConfig.productCategories[selectedCategory]?.description || seoConfig.pages.products.description :
        seoConfig.pages.products.description,
      keywords: combinedKeywords,
      breadcrumbs: selectedCategory ? [
        { name: "Home", url: "/" },
        { name: "Products", url: "/products" },
        { name: productCategories.find(c => c.id === parseInt(selectedCategory))?.name || "Category", url: `/products?category=${selectedCategory}` }
      ] : seoConfig.pages.products.breadcrumbs
    };
  };

  const seoData = getEnhancedSEO();

  return (
    <ProductContainer maxWidth="lg">
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url={selectedCategory ? `/products?category=${selectedCategory}` : "/products"}
        breadcrumbs={seoData.breadcrumbs}
      />
      {/* Hero Banner */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
          color: "white",
          textAlign: "center",
          py: 6,
          px: 4,
          borderRadius: 2,
          mb: 4,
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Typography variant="h2" sx={{ fontWeight: 700, mb: 2 }}>
          Our Products
        </Typography>
        <Typography variant="h5" sx={{ opacity: 0.9, maxWidth: 600, mx: "auto" }}>
          Comprehensive range of industrial packaging solutions for cotton industry
        </Typography>
      </Box>

      {/* Filter Section */}
      <FilterSection>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Category</InputLabel>
              <Select
                value={selectedCategory}
                label="Filter by Category"
                onChange={(e) => handleCategoryChange(e.target.value)}
                startAdornment={<FilterList sx={{ mr: 1, color: 'text.secondary' }} />}
              >
                <MenuItem value="">All Categories</MenuItem>
                {productCategories.map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Search products..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Grid>
        </Grid>
      </FilterSection>

      {/* View Controls */}
      <ViewControls>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {filteredProducts.length} Products Found
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="grid" aria-label="grid view">
            <GridView sx={{ mr: 1 }} />
            Grid
          </ToggleButton>
          <ToggleButton value="list" aria-label="list view">
            <ViewList sx={{ mr: 1 }} />
            List
          </ToggleButton>
        </ToggleButtonGroup>
      </ViewControls>

      {/* Products Display */}
      {filteredProducts.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <Typography variant="h4" gutterBottom>
            No products found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Try adjusting your search or category filter.
          </Typography>
        </Box>
      ) : viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {filteredProducts.map((product, index) => (
            <ProductGridItem key={product.id} product={product} index={index} />
          ))}
        </Grid>
      ) : (
        <Box>
          {filteredProducts.map((product, index) => (
            <ProductListItem key={product.id} product={product} index={index} />
          ))}
        </Box>
      )}

      {/* Add to Quote Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Add to Quote
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedProduct.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {formatPrice(selectedProduct.price)} per {selectedProduct.unit} (Min Order: {selectedProduct.minOrder})
              </Typography>
              
              <TextField
                fullWidth
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                margin="normal"
                inputProps={{ min: selectedProduct.minOrder }}
                required
              />
              
              <TextField
                fullWidth
                label="Notes (Optional)"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                margin="normal"
                placeholder="Add any specific requirements or notes..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmAddToQuote} variant="contained">
            Add to Quote
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add SEO-Rich FAQ Section at the bottom */}
      <SEOContentSection />
    </ProductContainer>
  );
};

// New SEO-rich content component
const SEOContentSection = () => {
  return (
    <Box sx={{ mt: 8, mb: 4 }}>
      {/* Hidden FAQ Section for SEO */}
      <Box
        component="section"
        sx={{
          visibility: 'hidden',
          height: 0,
          overflow: 'hidden',
          '& *': { fontSize: 0, lineHeight: 0 }
        }}
        aria-hidden="true"
      >
        <Typography variant="h2">Frequently Asked Questions about Industrial Packaging Solutions</Typography>
        <Typography variant="h3">What are PET straps and their applications in cotton industry?</Typography>
        <Typography>
          PET straps, also known as polyester strapping bands or plastic strapping rolls, are premium quality
          industrial packaging materials specifically designed for cotton bale packaging, agricultural produce
          strapping, and textile industry applications. Our PET straps offer high tensile strength, eco-friendly
          recyclable material, and weather-resistant properties suitable for cotton ginning mills, pressing mills,
          and agricultural storage facilities across Maharashtra, Akola, and the cotton belt regions.
        </Typography>
        <Typography variant="h3">Why choose pneumatic strapping machines for cotton bale packaging?</Typography>
        <Typography>
          Pneumatic strapping machines, air-powered packaging tools, and automatic strapping equipment provide
          efficient, reliable, and cost-effective solutions for cotton bale packaging, agricultural produce binding,
          and industrial packaging applications. Our heavy-duty pneumatic tools are designed for ginning mills,
          cotton processing facilities, and bulk packaging operations, offering user-friendly operation, low
          maintenance requirements, and compatibility with various strapping materials including PET straps and
          metal strapping bands.
        </Typography>
        <Typography variant="h3">What types of bale covers are available for cotton storage and transportation?</Typography>
        <Typography>
          Our comprehensive range includes cotton bale covers, polyester bale covers, weather-resistant agricultural
          covers, UV-resistant storage covers, and custom-sized protection sheets. These covers provide superior
          protection for cotton bales during monsoon seasons, outdoor storage, warehouse storage, and long-distance
          transportation. Suitable for cotton ginning mills, pressing mills, agricultural cooperatives, and farming
          operations throughout Vidarbha, Maharashtra, and central India cotton-growing regions.
        </Typography>
        <Typography variant="h3">How do ginning washers improve cotton processing efficiency?</Typography>
        <Typography>
          High-grade leather ginning washers, cotton ginning machine parts, and textile processing accessories are
          essential components for maintaining optimal performance in cotton ginning operations. Our premium leather
          washers provide fine finish, rugged design, extended service life, and reliable sealing for ginning
          machinery, cotton processing equipment, and textile mill applications. These replacement parts and
          maintenance supplies ensure smooth operations for cotton mills, ginning facilities, and agricultural
          processing centers.
        </Typography>
        <Typography variant="h3">What are the advantages of hoop iron straps over other strapping materials?</Typography>
        <Typography>
          Hoop iron straps, galvanized iron strapping rolls, and metal strapping bands offer superior strength,
          rust-resistant coating, and cost-effective solutions for heavy-duty bale packaging applications. Ideal
          for cotton bale binding, agricultural produce securing, and industrial packaging where high tensile
          strength is required. Our iron strapping provides traditional reliability, weather resistance, and
          economical packaging solutions for cotton industry, agricultural sector, and rural industry applications
          across Maharashtra and cotton-producing regions.
        </Typography>
      </Box>
      {/* Visible SEO-optimized content */}
      <Container>
        <Typography variant="h4" component="h2" gutterBottom sx={{ mt: 6, mb: 3, textAlign: 'center' }}>
          Complete Industrial Packaging Solutions for Cotton Industry
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                🏭 Serving Cotton Belt Since 1970
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Specialized supplier of PET straps, bale covers, ginning washers, and pneumatic strapping
                machines for cotton ginning mills, pressing mills, and agricultural processing facilities
                across Maharashtra, Akola, Vidarbha, and central India cotton-growing regions.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom color="primary">
                📦 B2B Industrial Packaging
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Wholesale and bulk supply of industrial packaging materials, agricultural packaging solutions,
                and cotton industry supplies. Serving ginning mills, cotton cooperatives, textile processing
                units, and farming operations with reliable, cost-effective packaging solutions.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ProductPage;
